import React, { useState } from "react";
import Status from "@/src/components/Status/Status";
import _includes from "lodash/includes";
import { getCaseLink, getProductData } from "@/src/services/productService";
import { Link } from "react-router";
import { Button, Stack, Typography } from "@mui/material";
import _filter from "lodash/filter";
import DialogModal from "@/src/components/DialogModal/DialogModal";
import _map from "lodash/map";
import ServiceWorldCaseItem from "@/src/components/Serviceworld/MyCases/ServiceWorldCaseItem";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { MinimalCase } from "@/src/types/Entities/MinimalCase";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import ProductBoxGrid from "@/src/components/ProductBoxGrid/ProductBoxGrid";

// todo replace productClassName with correct type after productService refactor

type CaseConfirmationModalProps = {
  open: boolean;
  handleClose: () => void;
  productClassName: AbstractCase["name"];
  currentCases: MinimalCase[];
};

const CaseConfirmationModal = ({ open, handleClose, productClassName, currentCases }: CaseConfirmationModalProps) => {
  const action = (
    <Button
      component={Link}
      to={"/servicewelt/" + getProductData(productClassName, "urlPartProductCategory") + "/" + getProductData(productClassName, "urlPartShort") + "/antrag"}
      variant="contained"
      color="primary"
      fullWidth
    >
      Neuen Fall anlegen
    </Button>
  );

  return (
    <DialogModal open={open} title={"Neuen Fall anlegen?"} onClose={handleClose} actions={action}>
      <Typography sx={{ marginBottom: "1rem" }}>
        Sie haben bereits einen Fall im Bereich{" "}
        <strong>
          {getProductData(productClassName, "productCategory.label")} - {getProductData(productClassName, "label")}
        </strong>
        .
      </Typography>
      <Typography sx={{ marginBottom: "1rem" }}>
        Möchten Sie tatsächlich einen neuen Fall anlegen? Dann klicken Sie auf "Neuen Fall anlegen”. Ansonsten können Sie Ihren bestehenden Fall hier öffnen:
      </Typography>
      <Stack spacing={2} sx={{ padding: "0.25rem" }}>
        {_map(currentCases, (minimalCase) => (
          <ServiceWorldCaseItem
            key={minimalCase.id}
            minimalCase={minimalCase}
            listItemButtonConfig={{ getLink: (minimalCase: MinimalCase) => getCaseLink(minimalCase) }}
          />
        ))}
      </Stack>
    </DialogModal>
  );
};

const InitialStatusBox = ({ productClassName }: { productClassName: AbstractCase["name"] }) => {
  const [isCaseConfirmationOpen, setIsCaseConfirmationOpen] = useState(false);
  const [currentCases, setCurrentCases] = useState<MinimalCase[]>([]);
  const { data: minimalCases, isLoading } = useQuery({
    queryKey: queryKeys.collection("/collection/cases"),
    queryFn: () => fetchCollection("/collection/cases"),
  });

  if (isLoading) {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const currentCasesInternal = _filter(minimalCases, (minimalCase) => minimalCase.name === productClassName);
    if (currentCasesInternal.length > 0) {
      event.preventDefault();
      setCurrentCases(currentCasesInternal);
      setIsCaseConfirmationOpen(true);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant={"body2"} color={"primary"}>
        <strong>Neuen Fall anlegen</strong>
      </Typography>
      <Status maxPercentage={10} />
      <ProductBoxGrid
        items={[
          <Typography>Vervollständigen Sie hier ganz unkompliziert Ihre Angaben. Wir kümmern uns anschließend umgehend um Ihr Anliegen.</Typography>,
          <Button
            component={Link}
            to={
              "/servicewelt/" + getProductData(productClassName, "urlPartProductCategory") + "/" + getProductData(productClassName, "urlPartShort") + "/antrag"
            }
            onClick={handleClick}
            variant="contained"
            color="primary"
            fullWidth
            disabled={_includes(
              [
                "alimony",
                "death",
                "familyLawCase",
                "eventContract",
                "membershipContract",
                "otherContract",
                "providerContract",
                "dataLeakContract",
                "purchaseContract",
                "serviceContract",
                "subscriptionContract",
                "travelContract",
                "vehicleContract",
              ],
              productClassName,
            )}
          >
            Jetzt starten
          </Button>,
        ]}
      />
      <CaseConfirmationModal
        productClassName={productClassName}
        open={isCaseConfirmationOpen}
        handleClose={() => setIsCaseConfirmationOpen(false)}
        currentCases={currentCases}
      />
    </Stack>
  );
};

export default InitialStatusBox;
